/**
 * ReadableRegistry — context + hook primitives for the readable registry.
 *
 * Split from `ReadableProvider.tsx` because React Fast Refresh requires
 * `.tsx` files to export only React components. The Provider component stays
 * in the `.tsx` file; the context and hooks live here.
 *
 * Framework reference: docs/platform/chat-agent-framework.md § Stage 1.
 */

import { createContext, use } from 'react'

import type { Readable } from '@vvfoundry/chat-core'

/**
 * Internal context contract. The `register`/`unregister`/`update` APIs are
 * private to `useReadable` — the one reader is `useReadableSnapshotGetter`
 * (the dispatch-time accessor used by `useChat`).
 */
export interface ReadableRegistryValue {
  /** Register a new entry under `registrationId`. Called on mount. */
  register: (registrationId: string, readable: Readable) => void
  /** Update the entry stored under `registrationId`. Called on re-render when props change. */
  update: (registrationId: string, readable: Readable) => void
  /** Remove the entry stored under `registrationId`. Called on unmount. */
  unregister: (registrationId: string) => void
  /** Current de-duplicated readables; the most recent registration wins. */
  getSnapshot: () => Readable[]
}

export const ReadableRegistryContext = createContext<ReadableRegistryValue | null>(null)

/**
 * Internal — used by `useReadable` and `useReadableSnapshotGetter` to reach
 * the provider. Throws with a descriptive message if no provider is mounted,
 * so consumers can't silently register into the void.
 */
export function useReadableRegistry(): ReadableRegistryValue {
  const ctx = use(ReadableRegistryContext)
  if (!ctx) {
    throw new Error(
      'useReadable / useReadableSnapshotGetter must be used inside <ReadableProvider>. ' +
        'Mount it once near the root of the chat-aware tree.',
    )
  }
  return ctx
}

/**
 * Dispatch-time getter; reading it never re-renders the caller, so `useChat`
 * serializes readables without re-rendering on every keystroke-driven update.
 */
export function useReadableSnapshotGetter(): () => Readable[] {
  return useReadableRegistry().getSnapshot
}
